import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Privacy / HIPAA", "Security", "Workplace safety (OSHA)", "Clinical / patient", "Vendor / RCM", "Other"];
const SEVERITIES = ["LOW", "MEDIUM", "HIGH"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }) : "—");

function CloseControls({ inc, onSaved }) {
  const [open, setOpen] = useState(false);
  const [resolution, setResolution] = useState("");
  const close = async () => {
    try {
      const res = await axios.put(`${API}/incidents/${inc.id}/close`, { resolution });
      onSaved(res.data);
      toast.success(`${inc.incident_number} closed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close failed");
    }
  };
  if (!open) return <button className="vr-link" onClick={() => setOpen(true)} data-testid={`inc-close-${inc.incident_number}`}>Close with resolution…</button>;
  return (
    <div className="vr-form">
      <input className="inp" placeholder="Resolution / outcome" value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid={`inc-resolution-${inc.incident_number}`} />
      <div className="vr-btnrow">
        <button className="btn" onClick={close} disabled={!resolution.trim()} data-testid={`inc-close-save-${inc.incident_number}`}>Close incident</button>
        <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function IncidentLog({ active }) {
  const [rows, setRows] = useState(null);
  const [date, setDate] = useState("");
  const [type, setType] = useState("");
  const [severity, setSeverity] = useState("");
  const [description, setDescription] = useState("");
  const [action, setAction] = useState("");
  const [reportedBy, setReportedBy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchRows = useCallback(() => {
    axios.get(`${API}/incidents`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { if (active === "inc") fetchRows(); }, [active, fetchRows]);

  const canSubmit = date && type && severity && description.trim() && reportedBy.trim() && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/incidents`, { date, incident_type: type, severity, description, immediate_action: action, reported_by: reportedBy });
      toast.success(`${res.data.incident_number} logged — ${severity} ${type}`);
      setDate(""); setType(""); setSeverity(""); setDescription(""); setAction(""); setReportedBy("");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log incident");
    }
    setSubmitting(false);
  };

  const onSaved = (d) => setRows((p) => p.map((x) => (x.id === d.id ? d : x)));
  const openCount = (rows || []).filter((r) => r.status === "OPEN").length;

  return (
    <section className={`chapter utility${active === "inc" ? " active" : ""}`} id="inc">
      <div className="eyebrow">Working record · executes ECH-SOP-19.2 — every incident logged the same day it is known</div>
      <h1>Incident Log</h1>
      <p className="lede">Privacy, security, safety, clinical, and vendor incidents in one register: what happened, how bad, what was done immediately, and how it closed. HIGH-severity privacy/security items also start the breach-assessment clock (Chapter 6).</p>

      <div className="formcard" data-testid="inc-form">
        <div className="formcard-title">Log an incident</div>
        <div className="fld-grid three">
          <div className="fld"><label>Date</label><input className="inp" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="inc-date" /></div>
          <div className="fld"><label>Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="inp sel" data-testid="inc-type"><SelectValue placeholder="Type…" /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="fld"><label>Severity</label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="inp sel" data-testid="inc-severity"><SelectValue placeholder="Severity…" /></SelectTrigger>
              <SelectContent>{SEVERITIES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label>What happened</label>
          <textarea className="inp ta" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Facts only — who, what, when, systems or PHI involved" data-testid="inc-description" />
        </div>
        <div className="fld-grid">
          <div className="fld"><label>Immediate action taken</label><input className="inp" value={action} onChange={(e) => setAction(e.target.value)} placeholder="Containment / first response" data-testid="inc-action" /></div>
          <div className="fld"><label>Reported by</label><input className="inp" value={reportedBy} onChange={(e) => setReportedBy(e.target.value)} placeholder="Name / lane" data-testid="inc-reportedby" /></div>
        </div>
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="inc-submit">Log incident</button>
      </div>

      <h2>Register {rows ? `— ${openCount} open` : ""}</h2>
      <table data-testid="inc-table">
        <tbody>
          <tr><th>INC #</th><th>Date</th><th>Type</th><th>Sev.</th><th>Description / action</th><th>Reported by</th><th style={{ width: 230 }}>Status / resolution</th></tr>
          {rows === null ? (
            <tr><td colSpan={7}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={7}>No incidents logged.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td className="q">{r.incident_number}</td>
                <td>{r.date}</td>
                <td>{r.incident_type}</td>
                <td><span className={`vr-pill ${r.severity === "HIGH" ? "declined" : r.severity === "MEDIUM" ? "open" : "cleared"}`}>{r.severity}</span></td>
                <td>{r.description}{r.immediate_action ? <div className="cap-sub">Immediate: {r.immediate_action}</div> : null}</td>
                <td>{r.reported_by}{r.recorded_by ? <div className="cap-sub">acct: {r.recorded_by.name}</div> : null}</td>
                <td>
                  {r.status === "CLOSED" ? (
                    <>
                      <span className="vr-pill cleared" data-testid={`inc-status-${r.incident_number}`}>CLOSED {fmt(r.closed_at)}</span>
                      <div className="cap-sub">Resolution: {r.resolution}{r.closed_by ? ` — ${r.closed_by.name}` : ""}</div>
                    </>
                  ) : (
                    <>
                      <span className="vr-pill open" data-testid={`inc-status-${r.incident_number}`}>OPEN</span>
                      <CloseControls inc={r} onSaved={onSaved} />
                    </>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Retention: 10 years with compliance records; OSHA-recordable items also feed the OSHA log (SOP 9.2). Incidents report at every compliance committee meeting.</p>
    </section>
  );
}
